import { observable, observe, action, computed } from 'mobx'
import {
  Unit,
  IChildProduct,
  IProductMetaDataItem,
  INestedProduct,
} from '@getgreenline/homi-shared'
import { uploadImageAndGetCloudinaryUrl, isPendingTag } from '../../../../utilities/helpers'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { CLOUDINARY } from '../../../../constants/general'
import {
  LotModels,
  MetaDataModels,
  ProductModels,
  ProductV2Models,
  UnitModels,
} from '@getgreenline/products'
import {
  IBaseProduct,
  IProductMetaData,
  ProductType,
  EProductStrainType,
} from '@getgreenline/products/build/models/product'
import { LocationModels } from '@getgreenline/locations'
import {
  ILocationIdentifierOverride,
  ILocationIdentifierPair,
} from '../../../../types/locationIdentifierOverride'
import { ILocationTagAssignment } from '../../../../types/locationTagAssignment'

// Reference: https://github.com/darkskyapp/string-hash/blob/master/index.js
// Generates a numeric barcode string (min 11 chars) seeded by the given input.
export const generateBarcodeString = (seed: string): string => {
  const input = seed + new Date().toString()
  let hash = 5381
  let i = 100

  while (i) {
    hash = (hash * 33) ^ input.charCodeAt(--i)
  }

  hash = hash >>> 0
  let barcode = hash.toString()
  while (barcode.length < 11) {
    barcode += '0'
  }
  return barcode
}

type TOmitBaseProduct =
  | 'id'
  | 'categoryColorHex'
  | 'categoryName'
  | 'childProducts'
  | 'companyId'
  | 'complianceCategoryId'
  | 'complianceCategoryName'
  | 'createDate'
  | 'inBetweenPricing'
  | 'indexOrder'
  | 'isBatchTracked'
  | 'isGiftCard'
  | 'isMasterProduct'
  | 'isPricingGroup'
  | 'metaData'
  | 'overridePrices'
  | 'parentCategoryId'
  | 'parentCategoryName'
  | 'parentProductId'
  | 'parentProductName'
  | 'supplierName'
  | 'taxes'
  | 'taxGroupName'
  | 'updateDate'
  | 'wooCommerceProductId'
  | 'wooCommerceSyncDate'
  | 'cannabinoidMetaData'
  | 'tags'
  | 'globalProductId'
  | 'images'

export interface IProductNetworkObject
  extends Omit<ProductModels.INestedProduct, TOmitBaseProduct> {
  id: string | null
  metaData: ProductMetaDataItem[]
  cannabinoidMetaData: CannabinoidMetaDataItem[]
  tags: ProductModels.IProductTag[]
  globalProductId: string | null
  images: ProductV2Models.IProductImage[] | null
  locationIdentifierOverrides: CreateLocationIdentifierOverride[]
}

export interface INestedProductNetworkObject
  extends IProductNetworkObject,
    Pick<
      ProductModels.INestedProduct,
      'isMasterProduct' | 'wooCommerceProductId' | 'inBetweenPricing' | 'isBatchTracked'
    > {
  childProducts: IProductNetworkObject[]
}

export function getEditableChildProducts<T extends { isDeleted: boolean }>(product: {
  isDeleted: boolean
  childProducts: T[]
}): T[] {
  return product.isDeleted
    ? product.childProducts
    : product.childProducts.filter((child) => !child.isDeleted)
}

export const lowerCasedReservedKeys: string[] = Object.keys(
  MetaDataModels.MetaDataReservedKeys,
).map((key) => {
  const lowerCasedKeyValue: string = MetaDataModels.MetaDataReservedKeys[key].toLowerCase()

  return lowerCasedKeyValue
})

export class ProductMetaData {
  @observable
  metaData: ProductMetaDataItem[] = []

  @action
  addMetaDataItem = (metaDataItem: ProductMetaDataItem) => {
    this.metaData.push(metaDataItem)
  }

  @action
  setValue = (key: string, value: string | number | boolean | null) => {
    const metaDataItem = this.findMetaData(key)
    const val = value?.toString() || null

    if (metaDataItem) {
      metaDataItem.setValue(val)
    } else {
      const newMetaDataItem = new ProductMetaDataItem(key, val)
      this.addMetaDataItem(newMetaDataItem)
    }
  }

  @action
  removeMetaDataItem = (metaDataItem: ProductMetaDataItem) => {
    this.metaData = this.metaData.filter((item) => {
      return item.metaKey !== metaDataItem.metaKey
    })
  }

  @action
  toggleShowMaxMin = () => {
    const showMaxMinItem = this.findMetaData(MetaDataModels.MetaDataReservedKeys.SHOW_MAX_MIN)

    if (showMaxMinItem || showMaxMinItem === undefined) {
      if (showMaxMinItem === undefined) {
        this.setValue(MetaDataModels.MetaDataReservedKeys.SHOW_MAX_MIN, true)
      } else {
        const newValue = showMaxMinItem.metaValue === 'true' ? 'false' : 'true'
        showMaxMinItem.setValue(newValue)
      }
    } else {
      this.setValue(MetaDataModels.MetaDataReservedKeys.SHOW_MAX_MIN, false)
    }
  }

  @computed
  get unit() {
    const item = this.findMetaData(MetaDataModels.MetaDataReservedKeys.UNIT)

    return item ? (item.metaValue as UnitModels.ThcCbdUnit) : null
  }

  @computed
  get showMaxMin() {
    const item = this.findMetaData(MetaDataModels.MetaDataReservedKeys.SHOW_MAX_MIN)
    return !!item?.formattedBoolean
  }

  @computed
  get thc() {
    const item = this.findMetaData(MetaDataModels.MetaDataReservedKeys.THC)
    return item?.formattedNumber === undefined ? null : item.formattedNumber
  }

  @computed
  get minTHC() {
    const item = this.findMetaData(MetaDataModels.MetaDataReservedKeys.MIN_THC)
    return item?.formattedNumber === undefined ? null : item.formattedNumber
  }

  @computed
  get maxTHC() {
    const item = this.findMetaData(MetaDataModels.MetaDataReservedKeys.MAX_THC)
    return item?.formattedNumber === undefined ? null : item.formattedNumber
  }

  @computed
  get cbd() {
    const item = this.findMetaData(MetaDataModels.MetaDataReservedKeys.CBD)
    return item?.formattedNumber === undefined ? null : item.formattedNumber
  }

  @computed
  get minCBD() {
    const item = this.findMetaData(MetaDataModels.MetaDataReservedKeys.MIN_CBD)
    return item?.formattedNumber === undefined ? null : item.formattedNumber
  }

  @computed
  get maxCBD() {
    const item = this.findMetaData(MetaDataModels.MetaDataReservedKeys.MAX_CBD)
    return item?.formattedNumber === undefined ? null : item.formattedNumber
  }

  @computed
  get quantity() {
    const item = this.findMetaData(MetaDataModels.MetaDataReservedKeys.QUANTITY)
    return item?.formattedNumber === undefined ? null : item.formattedNumber
  }

  @computed
  get netContent() {
    const item = this.findMetaData(MetaDataModels.MetaDataReservedKeys.NET_CONTENT)
    return item?.formattedNumber === undefined ? null : item.formattedNumber
  }

  @computed
  get netContentUnit() {
    const item = this.findMetaData(MetaDataModels.MetaDataReservedKeys.NET_CONTENT_UNIT)
    return item?.metaValue
  }

  findMetaData = (key: string) =>
    this.metaData.find((item) => {
      return item.metaKey.toLowerCase() === key.toLowerCase()
    })
}

export class ProductMetaDataItem implements IProductMetaDataItem {
  @observable
  metaKey = ''

  @observable
  metaValue: string | null = null

  constructor(metaKey?: string, metaValue?: string | null) {
    this.metaKey = metaKey || ''
    this.metaValue = metaValue || null
  }

  @action
  setKey = (key: string) => {
    this.metaKey = key
  }

  @action
  setValue = (value: string | null) => {
    this.metaValue = value
  }

  @computed
  get formattedNumber() {
    if (!lowerCasedReservedKeys.includes(this.metaKey.toLowerCase())) return

    const nonFloatKeys = [
      MetaDataModels.MetaDataReservedKeys.UNIT,
      MetaDataModels.MetaDataReservedKeys.SHOW_MAX_MIN,
    ]

    if (nonFloatKeys.some((key) => key === this.metaKey)) return

    if (this.metaValue === null) return

    try {
      return parseFloat(this.metaValue)
    } catch (err) {
      return
    }
  }

  @computed
  get formattedBoolean() {
    if (this.metaKey === MetaDataModels.MetaDataReservedKeys.SHOW_MAX_MIN) {
      return this.metaValue === 'true'
    }
  }
}

export class CannabinoidMetaData {
  @observable
  cannabinoids: CannabinoidMetaDataItem[] = []

  @action
  addCannabinoid = (unit: UnitModels.ThcCbdUnit | null, showMaxMin: boolean) => {
    const cannabinoid = new CannabinoidMetaDataItem()

    cannabinoid.setValue(MetaDataModels.MetaDataReservedKeys.SHOW_MAX_MIN, showMaxMin)
    cannabinoid.setValue(
      MetaDataModels.MetaDataReservedKeys.UNIT,
      unit || UnitModels.ThcCbdUnit.PERCENT,
    )

    this.cannabinoids.push(cannabinoid)
  }

  @action
  addCannabinoidMetaData = ({
    unit,
    showMaxMin = false,
    minTHC,
    maxTHC,
    minCBD,
    maxCBD,
  }: {
    unit: UnitModels.ThcCbdUnit | null
    showMaxMin?: boolean
    minTHC?: number | null
    maxTHC?: number | null
    minCBD?: number | null
    maxCBD?: number | null
  }) => {
    const cannabinoid = new CannabinoidMetaDataItem()
    cannabinoid.setValue(
      MetaDataModels.MetaDataReservedKeys.UNIT,
      unit || UnitModels.ThcCbdUnit.PERCENT,
    )
    cannabinoid.setValue(MetaDataModels.MetaDataReservedKeys.SHOW_MAX_MIN, showMaxMin)
    cannabinoid.setValue(MetaDataModels.MetaDataReservedKeys.MIN_THC, minTHC?.toString() || null)
    cannabinoid.setValue(MetaDataModels.MetaDataReservedKeys.MAX_THC, maxTHC?.toString() || null)
    cannabinoid.setValue(MetaDataModels.MetaDataReservedKeys.MIN_CBD, minCBD?.toString() || null)
    cannabinoid.setValue(MetaDataModels.MetaDataReservedKeys.MAX_CBD, maxCBD?.toString() || null)
    this.cannabinoids.push(cannabinoid)
  }

  @action
  removeCannabinoid = (index: number) => {
    this.cannabinoids.splice(index, 1)
  }

  // Collapse any entries sharing a batchNumber into a single cnb so the UI renders one
  // card per unique batchNumber and the PUT payload can't trip the duplicate-record
  // unique constraint on lotMetaData. First-wins for metaData; locationIds are unioned.
  // Entries with a null/undefined batchNumber are left alone (no key to merge on — the
  // backend assigns batchNumbers on save).
  @action
  dedupeByBatchNumber = () => {
    const byBatchNumber = new Map<string, CannabinoidMetaDataItem>()
    const result: CannabinoidMetaDataItem[] = []

    for (const cnb of this.cannabinoids) {
      if (!cnb.batchNumber) {
        result.push(cnb)
        continue
      }
      const existing = byBatchNumber.get(cnb.batchNumber)
      if (!existing) {
        byBatchNumber.set(cnb.batchNumber, cnb)
        result.push(cnb)
        continue
      }
      const merged = new Set<number>(existing.locationIds)
      cnb.locationIds.forEach((id) => merged.add(id))
      existing.locationIds = Array.from(merged)
    }

    this.cannabinoids = result
  }

  get locationMappedCannabinoidMetaData() {
    const mappedCannabinoidMetaData = new Map<number, CannabinoidMetaDataItem>()

    this.cannabinoids
      .filter((cnb) => cnb.isLocationCannabinoid !== false)
      .forEach((cnb) => {
        cnb.locationIds.forEach((locationId) => {
          mappedCannabinoidMetaData.set(locationId, cnb)
        })
      })

    return mappedCannabinoidMetaData
  }

  get batchMappedCannabinoidMetaData() {
    const mappedCannabinoidMetaData = new Map<string, CannabinoidMetaDataItem>()

    this.cannabinoids.forEach((cnb) => {
      if (cnb.batchNumber) {
        mappedCannabinoidMetaData.set(cnb.batchNumber, cnb)
      }
    })

    return mappedCannabinoidMetaData
  }
}

// At the time of profile creation, there is no `productId`
interface ICannabinoidMetaDataItem
  extends Omit<LotModels.ICreateLotContract, 'productId' | 'companyId'> {
  id?: number
}

export class CannabinoidMetaDataItem extends ProductMetaData implements ICannabinoidMetaDataItem {
  id?: number

  @observable
  batchNumber?: string

  @observable
  locationIds: number[] = []

  @observable
  isLocationCannabinoid?: boolean

  setLocationIds = (locationIds: number[]) => {
    this.locationIds = locationIds
  }

  setBatchNumber = (batchNumber: string | undefined) => {
    this.batchNumber = batchNumber
  }

  @action
  addLocationId = (locationId: number) => {
    this.locationIds.push(locationId)
  }

  @action
  removeLocationId = (locationId: number) => {
    this.locationIds = this.locationIds.filter((locId) => locId !== locationId)
  }

  @action
  populate = (lotContract: LotModels.ILotContract, defaultMetaData: IProductMetaData) => {
    this.id = lotContract.id
    this.batchNumber = lotContract.batchNumber
    this.locationIds = lotContract.locationIds
    this.isLocationCannabinoid =
      lotContract.isLocationCannabinoid !== null ? lotContract.isLocationCannabinoid : undefined
    this.populateMetaData(lotContract.metaData, defaultMetaData)
  }

  private formatMetaDataValue = (value: number | null) =>
    value === null ? 'unset' : `${value}${this.unit || UnitModels.ThcCbdUnit.PERCENT}`

  @computed
  get thcRange() {
    return this.showMaxMin
      ? `${this.formatMetaDataValue(this.minTHC)} - ${this.formatMetaDataValue(this.maxTHC)}`
      : `${this.formatMetaDataValue(this.thc)}`
  }

  @computed
  get cbdRange() {
    return this.showMaxMin
      ? `${this.formatMetaDataValue(this.minCBD)} - ${this.formatMetaDataValue(this.maxCBD)}`
      : `${this.formatMetaDataValue(this.cbd)}`
  }

  @computed
  get thcCbdRangeInfo() {
    return `THC: ${this.thcRange} | CBD: ${this.cbdRange}`
  }

  @action
  populateMetaData = (
    metaData: LotModels.ILotMetaDataContract[],
    defaultMetaData: IProductMetaData,
  ) => {
    const populatedMetaData: ProductMetaDataItem[] = metaData.map((item) => {
      return new ProductMetaDataItem(item.metaKey, item.metaValue)
    })

    if (
      populatedMetaData.some((x) => x.metaKey === MetaDataModels.MetaDataReservedKeys.UNIT) &&
      populatedMetaData.length > 0
    ) {
      this.metaData = populatedMetaData
      return
    }
    populatedMetaData.push(
      new ProductMetaDataItem(
        MetaDataModels.MetaDataReservedKeys.UNIT,
        defaultMetaData[MetaDataModels.MetaDataReservedKeys.UNIT],
      ),
    )

    this.metaData = populatedMetaData
  }
}

class MarginMarkupCalculator {
  @observable
  margin = 0

  @observable
  markup = 0

  private DECIMAL_PLACE = 1

  protected calculateMargin = ({
    price,
    purchasePrice,
    margin,
  }: {
    price: number | null
    purchasePrice: number | null
    margin?: number
  }) => {
    if (margin === undefined) {
      const sellPrice = price || 0
      const cost = purchasePrice || 0
      const calculatedMargin = sellPrice === 0 ? 0 : ((sellPrice - cost) / sellPrice) * 100

      return Number(calculatedMargin.toFixed(this.DECIMAL_PLACE))
    } else if (margin > 100) {
      return 100
    } else {
      return Number(margin.toFixed(this.DECIMAL_PLACE))
    }
  }

  protected calculateMarkup = ({
    price,
    purchasePrice,
    markup,
  }: {
    price: number | null
    purchasePrice: number | null
    markup?: number
  }) => {
    if (markup === undefined) {
      const sellPrice = price || 0
      const cost = purchasePrice || 0
      const calculatedMarkup = cost === 0 ? 0 : ((sellPrice - cost) / cost) * 100

      return Number(calculatedMarkup.toFixed(this.DECIMAL_PLACE))
    } else {
      return Number(markup.toFixed(this.DECIMAL_PLACE))
    }
  }

  protected calculatePriceFromMargin = (margin: number, purchasePrice: number | null) => {
    /**
     * If margin is 100%, this causes the denominator to become 0.
     * Since numerator / 0 is infinity, set margin to 99.99
     */
    const denominator = 1 - (margin === 100 ? 99.99 : margin) / 100

    const price = (purchasePrice || 0) / denominator

    return Math.round(price)
  }

  protected calculatePriceFromMarkup = (markup: number, purchasePrice: number | null) => {
    const price = (markup / 100 + 1) * (purchasePrice || 0)

    return Math.round(price)
  }
}

export class CreateLocationOverride
  extends MarginMarkupCalculator
  implements ProductModels.ILocationOverride
{
  @observable
  locationId: number

  @observable
  locationName: string

  @observable
  productId: string | null = null

  @observable
  retailPrice: number | null = null

  @observable
  wholesaleCost: number | null = null

  @observable
  sku: string | null = null

  @observable
  barcode: string | null = null

  constructor(overridePrice: ProductModels.ILocationOverride, purchasePrice: number | null) {
    super()

    this.locationId = overridePrice.locationId
    this.locationName = overridePrice.locationName || ''
    this.retailPrice = overridePrice.retailPrice ?? null
    this.wholesaleCost = overridePrice.wholesaleCost ?? null
    this.productId = overridePrice.productId || null
    this.sku = overridePrice.sku ?? null
    this.barcode = overridePrice.barcode ?? null

    this.setMargin(purchasePrice)
    this.setMarkup(purchasePrice)
  }

  @action
  setLocation(location: LocationModels.ILocationContract) {
    this.locationId = location.id
    this.locationName = location.name
  }

  @action
  setProductId(productId: string | null) {
    this.productId = productId
  }

  @action
  setRetailPrice(retailPrice: number | null) {
    this.retailPrice = retailPrice
  }

  @action
  setWholesaleCost(wholesaleCost: number | null) {
    this.wholesaleCost = wholesaleCost === 0 ? null : wholesaleCost
  }

  @action
  setMargin = (purchasePrice?: number | null, margin?: number) => {
    const cost = purchasePrice || this.wholesaleCost || 0

    this.margin = this.calculateMargin({
      price: this.retailPrice,
      purchasePrice: cost,
      margin,
    })

    if (margin !== undefined) {
      this.retailPrice = this.calculatePriceFromMargin(this.margin, cost)
      this.setMarkup(cost)
    }
  }

  @action
  setMarkup = (purchasePrice?: number | null, markup?: number) => {
    const cost = purchasePrice || this.wholesaleCost || 0

    this.markup = this.calculateMarkup({
      price: this.retailPrice,
      purchasePrice: cost,
      markup,
    })

    if (markup !== undefined) {
      this.retailPrice = this.calculatePriceFromMarkup(this.markup, cost)
      this.setMargin(cost)
    }
  }
}

export class CreateLocationIdentifierOverride implements ILocationIdentifierOverride {
  @observable
  locationId: number

  @observable
  locationName: string | null = null

  @observable
  productId: string | null = null

  @observable
  isActive: boolean | null = null

  @observable
  identifiers: ILocationIdentifierPair[] = []

  constructor(source: ILocationIdentifierOverride) {
    this.locationId = source.locationId
    this.locationName = source.locationName ?? null
    this.productId = source.productId ?? null
    this.isActive = source.isActive ?? null
    this.identifiers = (source.identifiers ?? []).map((p) => ({
      sku: p.sku ?? null,
      barcode: p.barcode ?? null,
    }))
  }
}

export interface ISlimTaxConfigResponseContract {
  id: number
  name: string
  rateAmount: number
  isActive: boolean
  categoryIds: string[]
  locationIds: number[]
  compoundingTaxes: number[]
}

export interface IOrgProductTaxLocation {
  id: number
  name: string
}

/**
 * One company's tax configuration breakdown for an org product, as returned by
 * the org product GET endpoints in `taxConfigsByCompany`. Tax configs are
 * company-scoped; this grouping prevents one company's configs from being
 * presented as org-wide (GR-13704).
 */
export interface IOrgProductCompanyTaxConfigs {
  companyId: number
  companyName: string
  categoryId: string | null
  categoryName: string | null
  taxConfigs: ISlimTaxConfigResponseContract[]
  locations: IOrgProductTaxLocation[]
}

export class CreateBaseProduct extends MarginMarkupCalculator {
  // True if any field has been modified after initialization, used to limit barcode printing without saves
  @observable
  changed = false

  // Populated from the API response on load (via Object.assign in populateFields). The
  // org product editor uses this to scope per-product API calls — notably the lot
  // dropdown's /batches endpoint, which needs the product's OWN orgProductGroupId, not
  // the parent's URL-param id, otherwise variants get the parent's batches.
  @observable
  orgProductGroupId: string | null = null

  @observable
  imageUrl: string | null = null

  @observable
  sku: string | null = null

  @observable
  barcode: string | null = null

  @observable
  name = ''

  @observable
  shortName = ''

  @observable
  description = ''

  @observable
  shortDescription = ''

  @observable
  inventorySubtractAmount = 1

  @observable
  unit: Unit = 'unit'

  @observable
  price: number | null = 0

  @observable
  purchasePrice: number | null = null

  @observable
  depositFee: number | null = null

  @observable
  weight: number | null = null

  @observable
  cannabisWeight: number | null = null

  @observable
  cannabisVolume: number | null = null

  @observable
  categoryId: string | null = null

  @observable
  complianceCategoryId: number | null = null

  @observable
  supplierId: number | null = null

  @observable
  taxGroupId: number | null = null

  @observable
  trackInventory = true

  @observable
  pricingGroupId: string | null = null

  originalPricingGroupId: string | null = null

  @observable
  isActive = true

  @observable
  isCumulative = false

  @observable
  trackPurchaseOrderCosts = true

  @observable
  trackConversionCosts = true

  @observable
  thc: number | null = null

  @observable
  cbd: number | null = null

  @observable
  metaData = new ProductMetaData()

  @observable
  cannabinoidMetaData = new CannabinoidMetaData()

  // this field will be used to determine whether to display warning message or not
  @observable
  isDeleted = false

  @observable
  isGiftCard = false

  @observable
  productType = ProductType.PRODUCT

  createDate?: string

  @observable
  strainType: EProductStrainType | null = null

  @observable
  tags: ProductModels.IProductTag[] = []

  @observable
  globalProductId: string | null = null

  @observable
  locationOverrides: CreateLocationOverride[] = []

  @observable
  locationIdentifierOverrides: CreateLocationIdentifierOverride[] = []

  @observable
  locationTagAssignments: ILocationTagAssignment[] = []

  @observable
  vendorIds: string[] | null = null

  @observable
  images: ProductV2Models.IProductImage[] | null = null

  @observable
  taxConfigs: ISlimTaxConfigResponseContract[] = []

  @observable
  taxConfigsByCompany: IOrgProductCompanyTaxConfigs[] = []

  @action
  setTags(tags: ProductModels.IProductTag[] | null) {
    tags = tags ?? []
    this.tags = tags
    const locationSpecific = this.locationTagAssignments.filter((a) => a.locationIds.length > 0)
    this.locationTagAssignments = [
      ...tags.map((t) => ({
        tagId: t.id != null ? String(t.id) : '',
        tagName: t.name,
        locationIds: [] as number[],
      })),
      ...locationSpecific,
    ]
  }

  @action
  removeLocationOverride(locationOverride: CreateLocationOverride) {
    const newOverridePrices = this.locationOverrides.filter(
      (locationOverrides) => locationOverrides.locationId !== locationOverride.locationId,
    )
    this.locationOverrides = newOverridePrices
  }

  @computed
  get overrideLocationIds() {
    return this.locationOverrides.map((locationOverride) => locationOverride.locationId)
  }

  @action
  setSKU(sku: string | null) {
    if (!sku) {
      sku = null
    }
    const cleanedSku = sku ? sku.replace(/\u200B/g, '') : null
    this.sku = cleanedSku
  }

  @action
  setGlobalProductId(globalProductId: string | null) {
    this.globalProductId = globalProductId
  }

  @action
  setBarcode(barcode: string | null) {
    if (!barcode) {
      barcode = null
    }
    const cleanedBarcode = barcode ? barcode.replace(/\u200B/g, '') : null
    this.barcode = cleanedBarcode
  }

  @action
  setName(name: string) {
    this.name = name
  }

  @action
  setShortName(shortName: string) {
    this.shortName = shortName
  }

  @action
  setDescription(description: string) {
    this.description = description
  }

  @action
  setShortDescription(shortDescription: string) {
    this.shortDescription = shortDescription
  }

  @action
  setInventorySubtractAmount(inventorySubtractAmount: number) {
    this.inventorySubtractAmount = inventorySubtractAmount
  }

  @action
  setPrice(price: number | null) {
    this.price = price

    this.setMargin()
    this.setMarkup()
  }

  @action
  setDepositFee(fee: number | null) {
    if (!fee) {
      fee = null
    }
    this.depositFee = fee
  }

  @action
  setPurchasePrice(price: number | null) {
    if (!price) {
      price = null
    }
    this.purchasePrice = price

    this.setMargin()
    this.setMarkup()
  }

  @action
  setMargin = (margin?: number) => {
    this.margin = this.calculateMargin({
      price: this.price,
      purchasePrice: this.purchasePrice,
      margin,
    })

    if (this.isGiftCard) {
      this.margin = 0
    }

    if (margin !== undefined) {
      this.price = this.calculatePriceFromMargin(this.margin, this.purchasePrice)
      this.setMarkup()
    }
  }

  @action
  setMarkup = (markup?: number) => {
    this.markup = this.calculateMarkup({
      price: this.price,
      purchasePrice: this.purchasePrice,
      markup,
    })

    if (this.isGiftCard) {
      this.markup = 0
    }

    if (markup !== undefined) {
      this.price = this.calculatePriceFromMarkup(this.markup, this.purchasePrice)
      this.setMargin()
    }
  }

  @action
  setWeight(weight: number | null) {
    if (!weight) {
      weight = null
    }
    this.weight = weight
  }

  @action
  setCannabisWeight(cannabisWeight: number | null) {
    if (!cannabisWeight) {
      cannabisWeight = null
    }
    this.cannabisWeight = cannabisWeight
  }

  @action
  setCannabisVolume(cannabisVolume: number | null) {
    if (!cannabisVolume) {
      cannabisVolume = null
    }
    this.cannabisVolume = cannabisVolume
  }

  @action
  setTrackPurchaseOrderCosts(trackPurchaseOrderCosts: boolean) {
    this.trackPurchaseOrderCosts = trackPurchaseOrderCosts
  }

  @action
  setTrackConversionCosts(trackConversionCosts: boolean) {
    this.trackConversionCosts = trackConversionCosts
  }

  @action
  setStrainType(strainType: EProductStrainType | null | undefined) {
    this.strainType = strainType ?? null
  }

  // Reference: https://github.com/darkskyapp/string-hash/blob/master/index.js
  @action
  generateBarcode() {
    this.barcode = generateBarcodeString(this.name)
  }

  @computed
  get isFreePricing(): boolean {
    return this.price === null
  }

  @action
  setLocationOverrides(locationOverrides: ProductModels.ILocationOverride[]) {
    this.locationOverrides = locationOverrides.map((locationOverride) => {
      return new CreateLocationOverride(locationOverride, this.purchasePrice)
    })
  }

  @action
  setLocationIdentifierOverrides(overrides: ILocationIdentifierOverride[]) {
    this.locationIdentifierOverrides = overrides.map((o) => new CreateLocationIdentifierOverride(o))
  }

  @action
  setLocationTagAssignments(assignments: ILocationTagAssignment[]) {
    this.locationTagAssignments = assignments
  }

  @action
  setImages(images: ProductV2Models.IProductImage[]) {
    this.images = images
  }

  @computed
  get hasLocationCannabinoids(): boolean {
    return this.cannabinoidMetaData.cannabinoids.some((cannabinoid) => {
      // Cannabinoid profile previously saved in DB
      if (cannabinoid.id) {
        return cannabinoid.isLocationCannabinoid === true
      }

      // If the cannabinoid profile was just created in the UI, id and isLocationCannabinoid are undefined
      // so we need to check the batch number and locationIds
      const hasNoBatchNumber =
        cannabinoid.batchNumber === undefined || cannabinoid.batchNumber === null
      const hasLocationIds = cannabinoid.locationIds && cannabinoid.locationIds.length > 0

      return hasNoBatchNumber && hasLocationIds
    })
  }
}

export class CreateProduct extends CreateBaseProduct {
  // If ID exists, the product is being edited. Otherwise, it is being created
  @observable
  readonly id: string | null = null

  @observable
  showAdvancedFields = false

  @observable
  isMasterProduct = true

  @observable
  childProducts: CreateChildProduct[] = []

  @observable
  wooCommerceProductId: number | null = null

  // this is used to determine how to display variants
  @observable
  needsPricingGroupRefresh = false

  @observable
  inBetweenPricing = false

  @observable
  isBatchTracked = false

  // Snapshot of isBatchTracked when the product was first loaded or created, before any update
  @observable
  originalIsBatchTracked = false

  constructor() {
    super()
    // Force inherited property consistencies
    this.setInheritedProperties()
    setTimeout(() => {
      observe(this, (change) => {
        if (change.name !== 'changed') {
          this.changed = true
        }
      })
    }, 1000)
  }

  @computed
  get inventoryMode(): boolean | 'bulk' | 'prepack' | undefined {
    if (this.isMasterProduct) {
      let temp: boolean | undefined = this.childProducts.length > 0 ? true : false
      this.childProducts.forEach((cp) => {
        if (cp.trackInventory === false) {
          temp = false
        }
      })
      if (this.childProducts.find((cp) => cp.trackInventory !== temp)) {
        temp = undefined
      }
      const variantsTrackInventory = temp
      if (variantsTrackInventory === undefined) {
        return undefined
      } else if (this.trackInventory === false && !variantsTrackInventory) {
        return false
      } else if (this.trackInventory === false && variantsTrackInventory) {
        return 'prepack'
      } else if (this.trackInventory === true && !variantsTrackInventory) {
        return 'bulk'
      } else {
        return undefined
      }
    } else {
      return this.trackInventory
    }
  }

  @action
  setInventoryMode(mode: boolean | 'bulk' | 'prepack') {
    if (mode === true || mode === false) {
      this.trackInventory = mode
      if (mode === true) {
        this.isMasterProduct = false
      }
      if (mode === false) {
        this.childProducts.forEach((cp) => {
          cp.trackInventory = false
        })
      }
    } else if (mode === 'bulk') {
      this.isMasterProduct = true
      this.trackInventory = true
      this.childProducts.forEach((cp) => {
        cp.trackInventory = false
      })
      this.setIsCumulative(false)
    } else if (mode === 'prepack') {
      this.isMasterProduct = true
      this.trackInventory = false
      this.setUnit('unit') // This will also set the inventory subtracted of parent/variants to be 1 as a side-effecet
      this.childProducts.forEach((cp) => {
        cp.trackInventory = true
      })
    }
  }

  @action
  unsetChanged() {
    this.childProducts.forEach((p) => {
      p.changed = false
    })
    this.changed = false
  }

  @computed
  get networkObject(): INestedProductNetworkObject {
    const allLocationsTags = this.locationTagAssignments
      .filter((a) => a.locationIds.length === 0 && !isPendingTag(a.tagId))
      .map(
        (a) =>
          ({
            ...(a.tagId ? { id: a.tagId } : {}),
            name: a.tagName ?? '',
          } as ProductModels.IProductTag),
      )

    const computedTags = allLocationsTags

    const overrideMap = new Map<number, ProductModels.ILocationOverride>()
    this.locationOverrides.forEach((o) => {
      overrideMap.set(o.locationId, { ...o })
    })

    this.locationTagAssignments
      .filter((a) => a.locationIds.length > 0 && !isPendingTag(a.tagId))
      .forEach((a) => {
        a.locationIds.forEach((locId) => {
          const existing = overrideMap.get(locId) ?? { locationId: locId }
          overrideMap.set(locId, existing)
          existing.tags = [
            ...(existing.tags ?? []),
            {
              ...(a.tagId ? { id: a.tagId } : {}),
              name: a.tagName ?? '',
            } as ProductModels.IProductTag,
          ]
        })
      })

    const mergedLocationOverrides = Array.from(overrideMap.values())

    return {
      id: this.id,
      imageUrl: this.imageUrl,
      sku: this.sku ? this.sku.trim() : null,
      barcode: this.barcode,
      name: this.name.trim(),
      shortName: this.shortName,
      description: this.description,
      shortDescription: this.shortDescription,
      inventorySubtractAmount: this.inventorySubtractAmount,
      unit: this.unit,
      price: this.price,
      depositFee: this.depositFee,
      purchasePrice: this.purchasePrice,
      weight: this.weight,
      cannabisWeight: this.cannabisWeight,
      cannabisVolume: this.cannabisVolume,
      categoryId: this.categoryId,
      supplierId: this.supplierId,
      taxGroupId: this.taxGroupId,
      trackInventory: this.trackInventory,
      isMasterProduct: this.isMasterProduct,
      childProducts: this.childProducts.map((cp) => cp.networkObject),
      wooCommerceProductId: this.wooCommerceProductId,
      pricingGroupId: this.pricingGroupId,
      pricingTemplateId: null,
      inBetweenPricing: this.inBetweenPricing,
      isActive: this.isActive,
      trackPurchaseOrderCosts: this.trackPurchaseOrderCosts,
      trackConversionCosts: this.trackConversionCosts,
      isBatchTracked: this.isBatchTracked,
      thc: this.thc,
      cbd: this.cbd,
      isCumulative: this.isCumulative,
      metaData: this.metaData.metaData,
      cannabinoidMetaData: this.cannabinoidMetaData.cannabinoids,
      isDeleted: this.isDeleted,
      productType: this.productType,
      strainType: this.strainType,
      tags: computedTags,
      globalProductId: this.globalProductId,
      locationOverrides: mergedLocationOverrides,
      locationIdentifierOverrides: this.locationIdentifierOverrides,
      vendorIds: this.vendorIds,
      images: this.images,
      taxConfigs: this.taxConfigs,
      normalizedCategoryId: null,
      orgProductGroupId: null,
      parentOrgProductGroupId: null,
    } as INestedProductNetworkObject
  }

  @action
  populateFields(jsonFromAPI: any) {
    const {
      childProducts,
      overridePrices,
      locationOverrides,
      locationIdentifierOverrides,
      ...restOfObject
    } = jsonFromAPI

    Object.assign(this, restOfObject)

    this.originalIsBatchTracked = this.isBatchTracked

    this.setMargin()
    this.setMarkup()

    this.originalPricingGroupId = restOfObject.pricingGroupId

    this.locationOverrides = (locationOverrides || [])
      .filter(
        (o: any) =>
          o.retailPrice !== null ||
          o.wholesaleCost !== null ||
          o.sku !== null ||
          o.barcode !== null,
      )
      .map((overridePrice: CreateLocationOverride) => {
        return new CreateLocationOverride(overridePrice, this.purchasePrice)
      })

    this.setLocationIdentifierOverrides(locationIdentifierOverrides || [])

    // Reconstruct locationTagAssignments from top-level tags and locationOverrides.
    // Key by lowercased name so that the same tag across org companies (different IDs, same name)
    // collapses into one entry — preventing UUID display for cross-company tags.
    const tagMap = new Map<string, ILocationTagAssignment>()

    ;(this.tags ?? []).forEach((tag: ProductModels.IProductTag) => {
      tagMap.set(tag.name.toLowerCase(), { tagId: tag.id, tagName: tag.name, locationIds: [] })
    })
    ;(locationOverrides ?? []).forEach((override: ProductModels.ILocationOverride) => {
      ;(override.tags ?? []).forEach((tag: ProductModels.IProductTag) => {
        const key = tag.name.toLowerCase()
        if (!tagMap.has(key)) {
          tagMap.set(key, {
            tagId: tag.id,
            tagName: tag.name,
            locationIds: [override.locationId],
          })
        } else {
          const existing = tagMap.get(key)
          if (existing) {
            existing.locationIds.push(override.locationId)
          }
        }
      })
    })

    this.setLocationTagAssignments(Array.from(tagMap.values()))

    const metaDataObj = new ProductMetaData()

    Object.keys(this.metaData).map((key) => {
      if (typeof this.metaData[key] === 'string') {
        const metaDataItem = new ProductMetaDataItem(key, this.metaData[key])
        metaDataObj.addMetaDataItem(metaDataItem)
      }
    })

    this.metaData = metaDataObj

    this.cannabinoidMetaData = this.getCannabinoidMetaData(restOfObject)

    this.childProducts = childProducts.map((childProduct: any) => {
      const {
        overridePrices: childOverridePrices,
        locationOverrides: childLocationOverrides,
        ...restOfChildProduct
      } = childProduct
      const observableChildProduct = new CreateChildProduct(this)

      Object.assign(observableChildProduct, restOfChildProduct)

      observableChildProduct.setMargin()
      observableChildProduct.setMarkup()

      if (childLocationOverrides) {
        const observableLocationOverrides = childLocationOverrides
          .filter(
            (o: any) =>
              o.retailPrice !== null ||
              o.wholesaleCost !== null ||
              o.sku !== null ||
              o.barcode !== null,
          )
          .map((overridePrice: CreateLocationOverride) => {
            return new CreateLocationOverride(overridePrice, childProduct.purchasePrice)
          })

        Object.assign(observableChildProduct, { locationOverrides: observableLocationOverrides })
      }

      observableChildProduct.setLocationIdentifierOverrides(
        childProduct.locationIdentifierOverrides || [],
      )

      const childTagMap = new Map<string, ILocationTagAssignment>()
      ;(childProduct.tags ?? []).forEach((tag: ProductModels.IProductTag) => {
        childTagMap.set(tag.name.toLowerCase(), {
          tagId: tag.id,
          tagName: tag.name,
          locationIds: [],
        })
      })
      ;(childProduct.locationOverrides ?? []).forEach(
        (override: ProductModels.ILocationOverride) => {
          ;(override.tags ?? []).forEach((tag: ProductModels.IProductTag) => {
            const key = tag.name.toLowerCase()
            if (!childTagMap.has(key)) {
              childTagMap.set(key, {
                tagId: tag.id,
                tagName: tag.name,
                locationIds: [override.locationId],
              })
            } else {
              const existing = childTagMap.get(key)
              if (existing) {
                existing.locationIds.push(override.locationId)
              }
            }
          })
        },
      )
      observableChildProduct.setLocationTagAssignments(Array.from(childTagMap.values()))

      const metaData = new ProductMetaData()

      if (restOfChildProduct.metaData) {
        Object.keys(restOfChildProduct.metaData).map((key) => {
          const metaDataItem = new ProductMetaDataItem(key, restOfChildProduct.metaData[key])
          metaData.addMetaDataItem(metaDataItem)
        })
      }

      Object.assign(observableChildProduct, { metaData })

      observableChildProduct.originalPricingGroupId = restOfChildProduct.originalPricingGroupId
      observableChildProduct.cannabinoidMetaData = this.getCannabinoidMetaData(restOfChildProduct)

      const inheritsParentImage =
        restOfChildProduct.imageUrl != null && restOfChildProduct.imageUrl === this.imageUrl

      observableChildProduct.shouldInheritParentImage = inheritsParentImage

      return observableChildProduct
    })
  }

  getCannabinoidMetaData = (product: IBaseProduct) => {
    const metaData = new CannabinoidMetaData()

    // Fallback of `[]` is required because `populateField` method accepts a parameter of `any` (poor code)
    // This is called in `EditPricingGroupModal` where the api call doesn't contain `cannabinoidMetaData`
    metaData.cannabinoids = (product.cannabinoidMetaData || []).map(
      (cnb: LotModels.ILotContract) => {
        const item = new CannabinoidMetaDataItem()
        item.populate(cnb, product.metaData)
        return item
      },
    )

    return metaData
  }

  @action
  undelete(product: INestedProduct) {
    this.networkObject.isDeleted = false
    this.networkObject.isActive = true
    if (product.isMasterProduct) {
      this.networkObject.childProducts.forEach((child: any) => {
        child.isDeleted = false
        child.isActive = true
      })
    }
    if (product.parentProductId) {
      this.networkObject.childProducts.forEach((child: any) => {
        if (child.id === product.id) {
          child.isDeleted = false
          child.isActive = true
        }
      })
    }
    return this.networkObject
  }

  @action
  toggleAdvancedFields() {
    this.showAdvancedFields = !this.showAdvancedFields
  }

  @action
  setImageUrl(imageUrl: string | null, options?: { propagateToChildren?: boolean }) {
    if (!imageUrl) {
      imageUrl = null
    }
    this.imageUrl = imageUrl
    if (options?.propagateToChildren === false) {
      return
    }
    this.childProducts.forEach((childProduct) => {
      if (childProduct.shouldInheritParentImage) {
        childProduct.setImageUrl(imageUrl, { inherited: true })
      }
    })
  }

  @action
  setUnit(unit: Unit) {
    this.unit = unit
    if (unit === 'unit') {
      this.inventorySubtractAmount = 1
    }
    this.childProducts.forEach((childProduct) => {
      childProduct.unit = unit
      if (unit === 'unit') {
        childProduct.inventorySubtractAmount = 1
      }
    })
  }

  @action
  setCategoryId(categoryId: string | null) {
    this.categoryId = categoryId
    this.childProducts.forEach((childProduct) => {
      childProduct.categoryId = categoryId
    })
  }

  @action
  setSupplierId(supplierId: number | null) {
    this.supplierId = supplierId
    this.childProducts.forEach((childProduct) => {
      childProduct.supplierId = supplierId
    })
  }

  @action
  setTaxGroupId(taxGroupId: number | null) {
    this.taxGroupId = taxGroupId
    this.childProducts.forEach((childProduct) => {
      childProduct.taxGroupId = taxGroupId
    })
  }

  @action
  setTrackInventory(trackInventory: boolean) {
    this.trackInventory = trackInventory
    if (trackInventory) {
      this.childProducts.forEach((childProduct) => {
        childProduct.trackInventory = false
      })
    }
  }

  @action
  setIsMasterProduct(isMasterProduct: boolean) {
    this.isMasterProduct = isMasterProduct
    if (isMasterProduct) {
      this.price = 0
      this.barcode = null

      // Default is prepack
      if (this.childProducts.length === 0) {
        this.addChildProduct()
        this.setInventoryMode('prepack')
      }
    } else {
      // Clear the child products array if the product is updated to not be a master product
      this.childProducts = []
      this.setInventoryMode(true)
    }
  }

  @action
  addChildProduct(initialFields?: IChildProduct) {
    const childProduct = new CreateChildProduct(this)
    if (initialFields) {
      Object.assign(childProduct, initialFields)
      childProduct.id = null
      childProduct.sku = null
      childProduct.setImageUrl(this.imageUrl, { inherited: true })
    }
    childProduct.unit = this.unit
    childProduct.categoryId = this.categoryId
    childProduct.supplierId = this.supplierId
    childProduct.taxGroupId = this.taxGroupId
    if (this.trackInventory) {
      childProduct.trackInventory = false
    } else {
      childProduct.trackInventory = true
    }
    if (!initialFields) {
      childProduct.setImageUrl(this.imageUrl, { inherited: true })
    }
    this.childProducts.push(childProduct)
    this.isActive = true
    return childProduct
  }

  @action
  removeChildProduct(index: number) {
    this.childProducts.splice(index, 1)
    this.updateActiveStatus()
  }

  @action
  clearAllChildProducts() {
    this.childProducts = []
  }

  @action
  setWooCommerceProductId(wooCommerceProductId: number | null) {
    if (!wooCommerceProductId) {
      wooCommerceProductId = null
    }
    this.wooCommerceProductId = wooCommerceProductId
  }

  @action
  setPricingGroupId(pricingGroupId: string | null) {
    const newPricingGroupDiffFromThis = this.pricingGroupId !== pricingGroupId
    const newPricingGroupDiffFromOriginal = this.originalPricingGroupId !== pricingGroupId

    if (
      pricingGroupId &&
      newPricingGroupDiffFromThis &&
      newPricingGroupDiffFromOriginal &&
      this.id
    ) {
      this.needsPricingGroupRefresh = true
    } else if (!pricingGroupId) {
      this.needsPricingGroupRefresh = false
      this.childProducts.forEach((cp) => {
        cp.pricingGroupId = null
      })
    } else {
      this.needsPricingGroupRefresh = false
    }

    this.pricingGroupId = pricingGroupId
  }

  @action
  setInBetweenPricing(inBetweenPricing: boolean) {
    this.inBetweenPricing = inBetweenPricing
  }

  @action
  setIsActive(isActive: boolean) {
    this.isActive = isActive
    this.childProducts.forEach((cp) => {
      cp.setIsActive(isActive)
    })
  }

  @action
  setIsCumulative(isCumulative: boolean) {
    this.isCumulative = isCumulative
    this.childProducts.forEach((cp) => {
      cp.setIsCumulative(isCumulative)
    })
  }

  @action
  setIsBatchTracked(isBatchTracked: boolean) {
    this.isBatchTracked = isBatchTracked
  }

  @action
  async setInheritedProperties(currentCompanyStore?: CurrentCompanyStore) {
    // Clean parent product SKU
    if (!this.sku) {
      this.sku = null
    }
    /*
     * Image inheritance rules:
     * - The UI only exposes a single image uploader while variants are first created, so every new variant
     *   starts by mirroring the parent image.
     * - As soon as an editor removes or replaces a variant image, we flip `shouldInheritParentImage` off,
     *   meaning subsequent parent updates or saves will no longer overwrite the variant.
     * - Variants that still have the flag set will continually receive whatever image the parent currently uses.
     */
    for (const childProduct of this.childProducts) {
      childProduct.unit = this.unit
      childProduct.supplierId = this.supplierId
      childProduct.categoryId = this.categoryId
      childProduct.taxGroupId = this.taxGroupId
      if (childProduct.shouldInheritParentImage) {
        childProduct.setImageUrl(this.imageUrl, { inherited: true })
      }
      if (childProduct.imageUrl && !childProduct.imageUrl.includes(CLOUDINARY)) {
        try {
          childProduct.imageUrl = await uploadImageAndGetCloudinaryUrl(
            childProduct.imageUrl,
            currentCompanyStore!,
          )
        } catch (error) {
          childProduct.imageUrl = null
          console.log(error)
        }
      }

      if (this.isMasterProduct && this.trackInventory) {
        childProduct.trackInventory = false
      }
      if (!childProduct.sku) {
        childProduct.sku = null
      }

      /*
      When a customer removes an input value inside <CurrencyInput />, it returns `null` instead of 0.
      We don't want child products' prices to be null, so this should be manually set to 0.
      */
      if (!childProduct.price) {
        childProduct.setPrice(0)
      }
    }
  }

  @action
  updateActiveStatus = () => {
    if (this.childProducts.length === 0) {
      return
    }

    const firstActiveVariant = this.childProducts.find((child) => child.isActive)
    this.isActive = firstActiveVariant !== undefined
  }

  @action
  setIsDeleted(isDeleted: boolean) {
    this.isDeleted = isDeleted
  }

  @action
  setVendorIds(vendorIds: string[] | null) {
    this.vendorIds = vendorIds
  }

  @computed
  get hasIncorrectCannabinoids(): boolean {
    const flattenedProducts = [...this.childProducts, this]

    const hasLocationBasedCannabinoids = flattenedProducts
      .filter((product) => !product.isDeleted)
      .some((product) => product.hasLocationCannabinoids)

    if (this.originalIsBatchTracked === true && hasLocationBasedCannabinoids) {
      return true
    } else if (
      this.originalIsBatchTracked === false &&
      this.isBatchTracked === true &&
      hasLocationBasedCannabinoids
    ) {
      return true
    }

    return false
  }
}

export class CreateChildProduct extends CreateBaseProduct {
  id: string | null = null

  // Populated from the API on load (org product edit page) via populateFields'
  // Object.assign; used to split a variant via the org-level endpoints. The
  // networkObject getter intentionally serializes this as null on save.
  @observable orgProductGroupId: string | null = null

  parentProduct: CreateProduct

  @observable
  shouldInheritParentImage = true

  constructor(parentProduct: CreateProduct) {
    super()
    this.parentProduct = parentProduct
    setTimeout(() => {
      observe(this, () => {
        this.changed = true
        this.parentProduct.changed = true
      })
    }, 1000)
  }

  @computed
  get networkObject(): IProductNetworkObject {
    const allLocationsTags = this.locationTagAssignments
      .filter((a) => a.locationIds.length === 0 && !isPendingTag(a.tagId))
      .map(
        (a) =>
          ({
            ...(a.tagId ? { id: a.tagId } : {}),
            name: a.tagName ?? '',
          } as ProductModels.IProductTag),
      )

    const computedTags = allLocationsTags

    const overrideMap = new Map<number, ProductModels.ILocationOverride>()
    this.locationOverrides.forEach((o) => {
      overrideMap.set(o.locationId, { ...o })
    })

    this.locationTagAssignments
      .filter((a) => a.locationIds.length > 0 && !isPendingTag(a.tagId))
      .forEach((a) => {
        a.locationIds.forEach((locId) => {
          const existing = overrideMap.get(locId) ?? { locationId: locId }
          overrideMap.set(locId, existing)
          existing.tags = [
            ...(existing.tags ?? []),
            {
              ...(a.tagId ? { id: a.tagId } : {}),
              name: a.tagName ?? '',
            } as ProductModels.IProductTag,
          ]
        })
      })

    const mergedLocationOverrides = Array.from(overrideMap.values())

    return {
      id: this.id,
      imageUrl: this.imageUrl,
      sku: this.sku ? this.sku.trim() : null,
      barcode: this.barcode,
      name: this.name.trim(),
      shortName: this.shortName,
      description: this.description,
      shortDescription: this.shortDescription,
      inventorySubtractAmount: this.inventorySubtractAmount,
      unit: this.unit,
      price: this.price,
      depositFee: this.depositFee,
      purchasePrice: this.purchasePrice,
      weight: this.weight,
      cannabisWeight: this.cannabisWeight,
      cannabisVolume: this.cannabisVolume,
      categoryId: this.categoryId,
      supplierId: this.supplierId,
      taxGroupId: this.taxGroupId,
      trackInventory: this.trackInventory,
      pricingGroupId: this.pricingGroupId,
      pricingTemplateId: null,
      isActive: this.isActive,
      trackPurchaseOrderCosts: this.trackPurchaseOrderCosts,
      trackConversionCosts: this.trackConversionCosts,
      thc: this.thc,
      cbd: this.cbd,
      metaData: this.metaData.metaData,
      cannabinoidMetaData: this.cannabinoidMetaData.cannabinoids,
      isDeleted: this.isDeleted,
      productType: this.productType,
      strainType: this.strainType,
      isCumulative: this.isCumulative,
      tags: computedTags,
      globalProductId: this.globalProductId,
      locationOverrides: mergedLocationOverrides,
      locationIdentifierOverrides: this.locationIdentifierOverrides,
      vendorIds: this.vendorIds,
      images: this.images,
      taxConfigs: this.taxConfigs,
      normalizedCategoryId: null,
      orgProductGroupId: null,
      parentOrgProductGroupId: null,
    } as IProductNetworkObject
  }

  @action
  setImageUrl(imageUrl: string | null, options?: { inherited?: boolean }) {
    const normalized = imageUrl ?? null
    this.imageUrl = normalized
    this.shouldInheritParentImage = options?.inherited ? true : false
  }

  @action
  setTrackInventory(trackInventory: boolean) {
    this.trackInventory = trackInventory
    if (trackInventory) {
      this.parentProduct.trackInventory = false
    }
  }

  @action
  setPricingGroupId(pricingGroupId: string | null) {
    this.pricingGroupId = pricingGroupId
  }

  @action
  setIsActive(isActive: boolean) {
    this.isActive = isActive
    this.parentProduct.updateActiveStatus()
  }

  @action
  setIsCumulative(isCumulative: boolean) {
    this.isCumulative = isCumulative
  }

  @computed
  get getPrice(): number | null {
    return this.price
  }

  @action
  setIsDeleted(isDeleted: boolean) {
    this.isDeleted = isDeleted
  }

  @action
  setSupplierId(supplierId: number | null) {
    this.supplierId = supplierId
  }

  @action
  setVendorIds(vendorIds: string[] | null) {
    this.vendorIds = vendorIds
  }
}
